/**
 * The editorial slot. One game, argued for rather than merely listed.
 *
 * There is no ranking here and no "trending": with a single released title, a
 * top-ten would be a lie. What a visitor gets instead is the reason it was
 * built first and three checkable facts about what is inside it.
 *
 * When a mood filter excludes Mahjong, this is where the honest empty state
 * appears - the same slot, so the page does not jump.
 */

import { featuredGames, gamesWithMood, getCategory } from "@kikik/game-catalog";
import type { GameEntry, MoodId } from "@kikik/game-catalog";
import { useI18n } from "../../i18n";
import { TilePoster } from "../art/TilePoster";
import { Stamp } from "../Stamp";

const FACTS = ["fact1", "fact2", "fact3"] as const;

export interface PicksSectionProps {
  mood: MoodId | null;
  onOpenGame: (slug: string) => void;
}

export function PicksSection({ mood, onOpenGame }: PicksSectionProps) {
  const { t } = useI18n();
  const games: readonly GameEntry[] = mood ? gamesWithMood(mood) : featuredGames();
  const game = games[0];

  return (
    <section className="section section--picks" id="games" aria-labelledby="picks-title" data-reveal="out">
      <div className="shell">
        <p className="eyebrow">{t("portal.picks.eyebrow")}</p>
        <h2 className="section__title" id="picks-title">
          {t("portal.picks.title")}
        </h2>

        {game ? (
          <article className="pick" data-slug={game.slug}>
            <div className="pick__art">
              <TilePoster label={t(game.titleKey)} />
              <Stamp status={game.status} className="pick__stamp" />
            </div>

            <div className="pick__body">
              <p className="pick__shelf">{t(getCategory(game.category)!.nameKey)}</p>
              <h3 className="pick__title">{t(game.titleKey)}</h3>
              <p className="pick__tagline">{t(game.taglineKey)}</p>
              <p className="pick__quote">{t("portal.picks.why.body")}</p>

              <dl className="facts">
                {FACTS.map((fact) => (
                  <div className="facts__row" key={fact}>
                    <dt>{t(`portal.picks.${fact}.title`)}</dt>
                    <dd>{t(`portal.picks.${fact}.body`)}</dd>
                  </div>
                ))}
              </dl>

              <div className="pick__actions">
                <button
                  type="button"
                  className="btn btn--primary"
                  data-action="open-game"
                  onClick={() => onOpenGame(game.slug)}
                >
                  {t("portal.picks.cta")}
                </button>
                <p className="pick__platforms">
                  <span className="pick__platform-label">{t("portal.platform.label")}</span>
                  {game.platforms.map((platform) => (
                    <span key={platform} className="tag">
                      {t(`portal.platform.${platform}`)}
                    </span>
                  ))}
                </p>
              </div>
            </div>
          </article>
        ) : (
          <p className="empty" role="status">
            {t("portal.intent.empty")}
          </p>
        )}
      </div>
    </section>
  );
}
